import { useQuery } from '@tanstack/react-query';
import classNames from 'classnames';
import { useClients } from 'src/api';
import {
  DeploymentDisplayParameter,
  DeploymentHealthChart,
  DeploymentInstructions,
  DeploymentLog,
  DeploymentMetricsChart,
  DeploymentResources,
  DeploymentStatus,
  DeploymentUsageChart,
  HealthStatus,
  Icon,
  PropertyColumn,
  Spinner,
  TransientNavLink,
} from 'src/components';
import { useParam, useTypedParams } from 'src/hooks';
import { formatDateTime, last } from 'src/lib';
import { texts } from 'src/texts';

const DEPLOYING_REFETCH_INTERVAL_MS = 3000;

export const DeploymentPage = () => {
  const { deploymentId, teamId } = useTypedParams({ deploymentId: 'int', teamId: 'int' });
  const clients = useClients();
  const [tab, setTab] = useParam<'overview' | 'usage' | 'log'>('tab', 'overview');

  const { data: loadedStatus, isLoading: isLoadingStatus } = useQuery({
    queryKey: ['deployment-status', teamId, deploymentId],
    queryFn: () => clients.deployments.getDeploymentStatus(deploymentId),
  });

  const { data: deployment } = useQuery({
    queryKey: ['deployment', deploymentId],
    queryFn: () => clients.deployments.getDeployment(deploymentId),
    refetchInterval: (query) => {
      const status = query.state.data?.status;

      return status === 'Pending' || status === 'Running' ? DEPLOYING_REFETCH_INTERVAL_MS : false;
    },
  });

  const serviceId = deployment?.serviceId || 0;

  const { data: loadedService } = useQuery({
    queryKey: ['service-public-version', serviceId, deployment?.serviceVersion],
    queryFn: () => clients.services.getServicePublicVersion(serviceId, deployment!.serviceVersion),
    enabled: !!serviceId && !!deployment,
  });

  if (!deployment || !loadedService) {
    return <Spinner visible={true} />;
  }

  const isPending = deployment.status === 'Pending' || deployment.status === 'Running';
  const displayStatus = loadedStatus?.resources || [];
  const displayParameters = loadedService.parameters.filter((x) => x.display);
  const update = last(deployment.availableUpdates);

  return (
    <div className="flex flex-col gap-8">
      <div className="mb-4 flex h-10 items-center gap-4">
        <TransientNavLink className="btn btn-ghost btn-circle text-sm" to={`../${teamId}`}>
          <Icon icon="arrow-left" size={16} />
        </TransientNavLink>

        <div className="grow">
          <h2 className="text-2xl">
            {texts.deployments.deploymentHeadline} {deployment.name || deployment?.serviceName}
          </h2>
        </div>

        {deployment?.status === 'Completed' && (
          <TransientNavLink className="btn btn-success" to={'update'}>
            {update && <Icon icon="alert" size={14} />}
            {texts.deployments.edit}
          </TransientNavLink>
        )}
      </div>

      {!isPending && update && (
        <div role="alert" className="alert alert-success">
          <Icon icon="alert" />
          <span>{texts.deployments.updateAvailable}</span>
        </div>
      )}

      <div className="-mx-8 border-b border-gray-300 px-4">
        <div role="tablist" className="tabs tabs-border">
          <a role="tab" className={classNames('tab', { 'tab-active': tab === 'overview' })} onClick={() => setTab('overview')}>
            {texts.common.overview}
          </a>
          <a role="tab" className={classNames('tab', { 'tab-active': tab === 'usage' })} onClick={() => setTab('usage')}>
            {texts.common.usage}
          </a>
          <a role="tab" className={classNames('tab', { 'tab-active': tab === 'log' })} onClick={() => setTab('log')}>
            {texts.common.logViewer}
          </a>
        </div>
      </div>

      {tab === 'overview' && (
        <div className="flex flex-col gap-8">
          {isPending && (
            <div role="alert" className="alert alert-info">
              <Icon icon="info" />
              <span>{texts.deployments.deployingInfo}</span>
            </div>
          )}

          <div className="grid grid-cols-3 gap-6">
            <PropertyColumn label="Service Name" value={deployment.serviceName} />

            <PropertyColumn label="Service Version">
              <div className="badge badge-primary badge-sm me-1 rounded-full font-normal">{deployment.serviceVersion}</div>
            </PropertyColumn>

            <PropertyColumn label="Status">
              <DeploymentStatus status={deployment.status} />
            </PropertyColumn>

            <PropertyColumn label="Health">
              <div className="flex items-center gap-1">
                {deployment.healthStatus ? (
                  <>
                    <HealthStatus status={deployment.healthStatus} />
                    <a className="link" onClick={() => setTab('usage')}>
                      {texts.common.more}
                    </a>
                  </>
                ) : (
                  <>-</>
                )}
              </div>
            </PropertyColumn>

            <PropertyColumn label="Created" value={formatDateTime(deployment.createdAt)} />
            <PropertyColumn label="Name" value={deployment.name || '-'} />

            {displayParameters.map((parameter) => (
              <DeploymentDisplayParameter key={parameter.name} deployment={deployment} parameter={parameter} />
            ))}
          </div>

          {loadedService.afterInstallationInstructions && (
            <div>
              <h2 className="mb-3 flex items-center gap-3 text-xl">
                <Icon icon="info" size={16} className="inline-block" /> {texts.common.instructions}
              </h2>
              <div className="card card-border bg-base border-gray-300">
                <div className="card-body">
                  <DeploymentInstructions deployment={deployment} text={loadedService.afterInstallationInstructions} />
                </div>
              </div>
            </div>
          )}

          <div>
            <h2 className="mb-3 flex items-center gap-3 text-xl">
              <Icon icon="server" size={16} className="inline-block" /> {texts.deployments.resources}
            </h2>

            <DeploymentResources
              deployment={deployment}
              status={displayStatus}
              statusLoading={isLoadingStatus}
              isActive={isPending}
            />
          </div>
        </div>
      )}

      {tab === 'usage' && (
        <div className="flex flex-col gap-8">
          <div>
            <h2 className="mb-3 flex items-center gap-3 text-xl">
              <Icon icon="activity" size={16} className="inline-block" /> {texts.common.metrics}
            </h2>

            <DeploymentMetricsChart deploymentId={deploymentId} />

            <div className="mt-4 text-right text-xs">{texts.deployments.metricsChartWarning}</div>
          </div>

          {/* Usage only drives billing for pay-per-use services, so it is hidden for fixed pricing. */}
          {loadedService.pricingModel !== 'fixed' && (
            <div>
              <h2 className="mb-3 flex items-center gap-3 text-xl">
                <Icon icon="bar-chart" size={16} className="inline-block" /> {texts.common.usage}
              </h2>

              <DeploymentUsageChart deploymentId={deploymentId} />

              <div className="mt-4 text-right text-xs">{texts.deployments.usageChartWarning}</div>
            </div>
          )}

          <div id="health">
            <h2 className="mb-3 flex items-center gap-3 text-xl">
              <Icon icon="activity" size={16} className="inline-block" /> {texts.common.health}
            </h2>

            <DeploymentHealthChart deploymentId={deploymentId} />

            <div className="mt-4 text-right text-xs">{texts.deployments.checkChartWarning}</div>
          </div>
        </div>
      )}

      {tab === 'log' && <DeploymentLog deployment={deployment} />}
    </div>
  );
};
